import React, { type ReactElement } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import { isSameDay, isThisYear, parseISO } from 'date-fns'
import { type PointLike, type StyleSpecification } from 'react-map-gl/maplibre'
import { Map, type IControl } from 'maplibre-gl'
import { polygon } from '@turf/helpers'
import { centerOfMass } from '@turf/center-of-mass'
import { localDate } from 'global/helpers/dates'
import { getBaseUrl } from 'global/helpers/environment'
import theme from 'assets/theme'
import type { GeoPoint } from 'screens/TripDetails/TripDetails.types'
import type { MapMarker } from './types'
import {
  type MapIconProps,
  KNGenericMapIcon,
  KNGeofenceMapIcon,
  KNPickupMapIcon,
  KNDeliveryMapIcon,
  KNCustomsMapIcon,
  KNVehicleMapIcon,
  KNHeadingIcon,
  KNDwellMapIcon,
  KNFirstPositionIcon,
} from 'components/KNIcon/KNMapIcon'

const DEG_TO_RAD = Math.PI / 180
const RAD_TO_DEG = 180 / Math.PI

export const getDistance = (firstGeoPoint: GeoPoint, secondGeoPoint: GeoPoint): number => {
  const R = 6371.071 // radius of Earth in kilometers
  const firstLatitudeRadians = firstGeoPoint.latitude * DEG_TO_RAD
  const secondLatitudeRadians = secondGeoPoint.latitude * DEG_TO_RAD
  const latitudeDifference = secondLatitudeRadians - firstLatitudeRadians
  const longitudeDifference = (secondGeoPoint.longitude - firstGeoPoint.longitude) * DEG_TO_RAD
  return (
    2 *
    R *
    Math.asin(
      Math.sqrt(
        Math.sin(latitudeDifference / 2) * Math.sin(latitudeDifference / 2) +
        Math.cos(firstLatitudeRadians) *
        Math.cos(secondLatitudeRadians) *
        Math.sin(longitudeDifference / 2) *
        Math.sin(longitudeDifference / 2),
      ),
    )
  )
}

export const getHeading = (firstGeoPoint: GeoPoint, secondGeoPoint: GeoPoint): number => {
  const firstLatitudeRadians = firstGeoPoint.latitude * DEG_TO_RAD
  const secondLatitudeRadians = secondGeoPoint.latitude * DEG_TO_RAD
  const longitudeDifference = (secondGeoPoint.longitude - firstGeoPoint.longitude) * DEG_TO_RAD
  const y = Math.sin(longitudeDifference) * Math.cos(secondLatitudeRadians)
  const x =
    Math.cos(firstLatitudeRadians) * Math.sin(secondLatitudeRadians) -
    Math.sin(firstLatitudeRadians) * Math.cos(secondLatitudeRadians) * Math.cos(longitudeDifference)
  return (Math.atan2(y, x) * RAD_TO_DEG + 360) % 360
}

export const getMarkerTypeSize = (type?: string): number => {
  if (type === 'HEADING') {
    return 24
  }
  return 32
}

export const getMarkerTypeColor = (type?: string): string => {
  switch (type) {
    case 'PUP':
      return theme.palette.secondary.main
    case 'DEL':
      return theme.palette.primary.main
    case 'CUS':
      return theme.palette.secondary.main
    case 'VEHICLE':
      return theme.palette.success.main
    case 'HEADING':
      return theme.palette.primary.main
    case 'GEOFENCE':
      return theme.palette.primary.light
    case 'DWELL_TIME':
      return '#ffa8a8'
    default:
      return theme.palette.primary.light
  }
}

export const getMarkerTypeIconComponent = (type?: string): React.FC<MapIconProps> => {
  switch (type) {
    case 'PUP':
      return KNPickupMapIcon
    case 'DEL':
      return KNDeliveryMapIcon
    case 'CUS':
      return KNCustomsMapIcon
    case 'VEHICLE':
      return KNVehicleMapIcon
    case 'HEADING':
      return KNHeadingIcon
    case 'FIRST_POSITION':
      return KNFirstPositionIcon
    case 'GEOFENCE':
      return KNGeofenceMapIcon
    case 'PARKING_LOCATION':
      return KNGenericMapIcon
    case 'DWELL_TIME':
      return KNDwellMapIcon
    default:
      return KNGenericMapIcon
  }
}

export const getMarkerTypeLabel = (type?: string): string | undefined => {
  if (type === 'PARKING_LOCATION') {
    return 'P'
  }
  return undefined
}

export const getMarkerIcon = (marker: MapMarker, customSize?: number): ReactElement => {
  const size = customSize ?? getMarkerTypeSize(marker.type)
  const color = marker.color ?? getMarkerTypeColor(marker.type)
  const label = marker.label ?? getMarkerTypeLabel(marker.type)
  const MarkerIcon = getMarkerTypeIconComponent(marker.type)
  return <MarkerIcon size={size} color={color} label={label} state={marker.state} />
}

export const getMarkerOffset = (marker: MapMarker): PointLike => {
  switch (marker.type) {
    case 'VEHICLE':
    case 'HEADING':
      return [0, 0]
    case 'FIRST_POSITION':
      return [8, -8]
    default:
      return [0, -8]
  }
}

export const getHeadingDate = (timestamp: string, lastTimestamp?: string): string | null => {
  const parsedTimestamp = parseISO(timestamp)
  if (!parsedTimestamp) {
    return null
  }
  const fullDateFormat = isThisYear(parsedTimestamp) ? 'full_no_year' : 'full'
  const mediumDateFormat = isThisYear(parsedTimestamp) ? 'medium_no_year' : 'medium'
  if (!lastTimestamp) {
    return localDate(parsedTimestamp, fullDateFormat)
  }
  const parsedLastTimestamp = parseISO(lastTimestamp)
  if (!parsedLastTimestamp) {
    return null
  }
  if (isSameDay(parsedTimestamp, parsedLastTimestamp)) {
    return `${localDate(parsedTimestamp, mediumDateFormat)!} ${localDate(parsedTimestamp, 'time')!}-${localDate(parsedLastTimestamp, 'time')!}`
  }
  return `${localDate(parsedTimestamp, fullDateFormat)!} - ${localDate(parsedLastTimestamp, fullDateFormat)!}`
}

// partially based on maplibre-gl-js/src/ui/control/navigation_control.ts
export class BaseControl implements IControl {
  private readonly _container: HTMLElement
  private _root?: Root

  constructor() {
    this._container = document.createElement('div')
    this._container.className = 'maplibregl-ctrl maplibregl-ctrl-group'
    this._container.addEventListener('contextmenu', (e) => e.preventDefault())
  }

  onAdd(map: Map) {
    this._root = createRoot(this._container)
    return this._container
  }

  onRemove() {
    // defers unmount to next tick to avoid potential error logs
    setTimeout(() => {
      this._root?.unmount()
    }, 0)
    this._container.parentNode?.removeChild(this._container)
  }

  getRoot(): Root | undefined {
    return this._root
  }
}

export const processMapStyle = (style: StyleSpecification): StyleSpecification => {
  const baseUrl = getBaseUrl()
  return {
    ...style,
    // update sprite and glyphs from relative to absolute urls
    sprite: `${baseUrl}/map/${style.sprite as string}`,
    glyphs: `${baseUrl}/map/${style.glyphs as string}`,
  }
}

export const getPolygonCenter = (geoPoints: GeoPoint[]): GeoPoint => {
  const center = centerOfMass(
    polygon([
      geoPoints.map((geoPoint) => [geoPoint.longitude, geoPoint.latitude]),
    ]),
  )
  return {
    latitude: center.geometry.coordinates[1]!,
    longitude: center.geometry.coordinates[0]!,
  }
}
